import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { accessControlService } from '../../services/accessControlService';
import { getCurrentUserRole } from '../../utils/roleAccess';
import { hasFeatureAccess } from '../../utils/featureLock';
import { hasInvoiceScannerSubscription } from '../../utils/invoiceScannerAccess';
import { filterSettingsSections } from './settingsSections';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import './settingsLanding.css';

const SettingsLanding = () => {
  const [userRole, setUserRole] = useState(() => getCurrentUserRole());
  const [menuAccessKeys, setMenuAccessKeys] = useState(null);
  const [featureLocks, setFeatureLocks] = useState({});
  const [hasSnapBillsAccess, setHasSnapBillsAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setUserRole(getCurrentUserRole());
      try {
        const res = await accessControlService.getMyAccess();
        if (!cancelled && res?.data?.success) {
          setMenuAccessKeys(res.data.data?.menuKeys ?? []);
        } else if (!cancelled) {
          setMenuAccessKeys(null);
        }
      } catch {
        if (!cancelled) setMenuAccessKeys(null);
      }

      const keys = ['branch', 'department'];
      const locks = {};
      let snapAccess = false;
      await Promise.all([
        ...keys.map(async (key) => {
          try {
            locks[key] = !(await hasFeatureAccess(key));
          } catch {
            locks[key] = false;
          }
        }),
        (async () => {
          try {
            snapAccess = await hasInvoiceScannerSubscription();
          } catch {
            snapAccess = false;
          }
        })(),
      ]);
      if (!cancelled) {
        setFeatureLocks(locks);
        setHasSnapBillsAccess(snapAccess);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(
    () =>
      filterSettingsSections(userRole, menuAccessKeys, {
        featureAvailability: { invoice_scanner: hasSnapBillsAccess },
        hideLandingItems: true,
      }),
    [userRole, menuAccessKeys, hasSnapBillsAccess]
  );

  const resolveItemTo = (item) => {
    if (item.feature_key && featureLocks[item.feature_key]) {
      return `/subscription-feature-required?module=${item.feature_key}`;
    }
    return item.to;
  };

  if (loading) {
    return (
      <div className="zb-settings">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="zb-settings">
        <div className="zb-settings__grid">
          {sections.map((section) => (
            <section
              key={section.id}
              className="zb-settings__card"
              aria-labelledby={`settings-${section.id}`}
            >
              <header className="zb-settings__card-head">
                <span className="zb-settings__card-icon" aria-hidden="true">
                  <i className={section.icon} />
                </span>
                <h2 id={`settings-${section.id}`} className="zb-settings__card-title">
                  {section.title}
                </h2>
              </header>
              <ul className="zb-settings__list">
                {section.items.map((item) => {
                  const locked = !!(item.feature_key && featureLocks[item.feature_key]);
                  const to = resolveItemTo(item);
                  return (
                    <li key={item.id}>
                      <Link
                        to={to}
                        className={`zb-settings__link${locked ? ' is-locked' : ''}`}
                        title={
                          locked
                            ? 'This feature is not available in your current plan.'
                            : item.label
                        }
                      >
                        <span>{item.label}</span>
                        {locked ? (
                          <i className="fas fa-lock zb-settings__lock" aria-hidden="true" />
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
      <InternalFooter />
      <BackToTop />
    </>
  );
};

export default SettingsLanding;
